import {
  Children,
  cloneElement,
  FC,
  HTMLAttributes,
  isValidElement,
  PropsWithChildren,
  ReactElement,
  ReactNode,
  ThHTMLAttributes
} from 'react';

interface ITableSubComponent {
  Component: 'thead' | 'tbody';
}

// Type the ReactElement with proper HTML attributes
const isRow = (row: Parameters<typeof isValidElement>[0]): row is ReactElement<HTMLAttributes<HTMLTableRowElement>> =>
  isValidElement(row) && row.type === 'tr';

const isTh = (
  cell: Parameters<typeof isValidElement>[0]
): cell is ReactElement<ThHTMLAttributes<HTMLTableCellElement>> => isValidElement(cell) && cell.type === 'th';

const injectScope = (cell: ReactNode, scope: string): ReactNode => {
  if (isTh(cell)) {
    return cloneElement(cell, { scope } as ThHTMLAttributes<HTMLTableCellElement>);
  }

  return cell;
};

const TableSubComponent: FC<PropsWithChildren<ITableSubComponent>> = ({ Component, children }) => {
  const scope = Component === 'thead' ? 'col' : 'row';
  return (
    <Component>
      {Children.map(children, row => {
        if (isRow(row)) {
          // Now TypeScript knows row.props exists and has children
          return cloneElement(row, {
            children: Children.map(row.props.children, cell => injectScope(cell, scope))
          } as HTMLAttributes<HTMLTableRowElement>);
        }

        return row;
      })}
    </Component>
  );
};

TableSubComponent.displayName = 'TableSubComponent';

export const THead: FC<PropsWithChildren> = props => <TableSubComponent Component="thead" {...props} />;
THead.displayName = 'THead';

export const TBody: FC<PropsWithChildren> = props => <TableSubComponent Component="tbody" {...props} />;
TBody.displayName = 'TBody';
